import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { UserAuthLayout } from "../../components/user/UserAuthLayout";
import { GoogleLoginButton } from "../../components/user/GoogleLoginButton";
import { useUserAuth, formatApiErrorDetail } from "../../context/UserAuthContext";

export default function UserRegister() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { register } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from || "/conta";

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("A password tem de ter pelo menos 8 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await register(form);
      if (data.linked_tickets > 0) {
        toast.success(`Conta criada · ${data.linked_tickets} bilhete(s) já comprado(s) associado(s).`);
      } else {
        toast.success("Conta criada com sucesso");
      }
      navigate(redirect);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao criar conta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <UserAuthLayout
      title="Criar conta"
      subtitle="Acompanhe os seus bilhetes e simplifique futuras compras."
      footer={
        <>
          Já tem conta?{" "}
          <Link to="/login" className="text-amber-400 hover:text-amber-300" data-testid="link-login">
            Entrar
          </Link>
        </>
      }
    >
      <GoogleLoginButton label="Criar conta com Google" />
      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
        <div className="relative flex justify-center text-xs uppercase tracking-wider"><span className="bg-zinc-950 px-2 text-zinc-500">ou</span></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first_name" className="text-zinc-300">Primeiro nome</Label>
            <Input id="first_name" value={form.first_name} onChange={handleChange("first_name")} className="input-dark mt-1.5" autoComplete="given-name" data-testid="register-first-name" />
          </div>
          <div>
            <Label htmlFor="last_name" className="text-zinc-300">Último nome</Label>
            <Input id="last_name" value={form.last_name} onChange={handleChange("last_name")} className="input-dark mt-1.5" autoComplete="family-name" data-testid="register-last-name" />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-zinc-300">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={handleChange("email")} className="input-dark mt-1.5" autoComplete="email" data-testid="register-email" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-zinc-300">Telefone <span className="text-zinc-500">(opcional)</span></Label>
          <Input id="phone" type="tel" value={form.phone} onChange={handleChange("phone")} className="input-dark mt-1.5" placeholder="+351 912 345 678" autoComplete="tel" data-testid="register-phone" />
        </div>
        <div>
          <Label htmlFor="password" className="text-zinc-300">Password</Label>
          <Input id="password" type="password" required value={form.password} onChange={handleChange("password")} placeholder="Mínimo 8 caracteres" className="input-dark mt-1.5" autoComplete="new-password" data-testid="register-password" />
        </div>
        <Button type="submit" disabled={submitting || !form.email || !form.password} className="btn-primary w-full" data-testid="register-submit">
          {submitting ? "A criar conta..." : "Criar conta"}
        </Button>
      </form>
    </UserAuthLayout>
  );
}
